import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { VReadOnly } from '@client/components/_shared/forms/view-form/VReadOnly';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { createFormStore } from '@client/stores/FormStore';
import { type RouterOutputs, trpc } from '@client/trpc/client';
import { EXCESS_PAYMENT_METHODS_LABELS, EXCESS_PAYMENT_STATUSES_LABELS } from '@client/utils/financials';
import { toYesNo } from '@shared/utils/core/general';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Skeleton, Tag } from 'antd';
import { FileText, Receipt } from 'lucide-react';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/compensations/details/$compensationId/financials/excess-payments/details/$excessPaymentId',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Excess payment details',
        },
    },
    params: {
        parse({ excessPaymentId }) {
            return {
                excessPaymentId: BigInt(excessPaymentId),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ExcessPaymentDetails />
        </PageController>
    );
}

type ExcessPayment = NonNullable<RouterOutputs['excessPayment']['getExcessPayment']>;

const statusColors: Record<ExcessPayment['status'], string> = {
    FULLY_SETTLED: 'success',
    PENDING: 'blue',
};

function CardTitle({ icon: Icon, title }: { icon: typeof FileText; title: string }) {
    return (
        <div className="flex items-center gap-2">
            <Icon className="size-4 text-accent-500" />
            <span>{title}</span>
        </div>
    );
}

const excessPaymentFormStore = createFormStore<ExcessPayment>();

function ExcessPaymentDetails() {
    const { excessPaymentId } = Route.useParams();
    const { data, isPending, refetch } = trpc.excessPayment.getExcessPayment.useQuery({
        id: excessPaymentId,
    });
    const { formatCurrency } = useFormatter();
    const navigate = useNavigate();

    excessPaymentFormStore.useInit({
        data,
        isLoading: isPending,
        isReadOnly: true,
        async onReload() {
            await refetch();
        },
        async onSave() {
            return { success: true };
        },
    });

    return (
        <OverlayPage
            afterClose={() => navigate({ moveInMatches: -1 })}
            title="Excess payment details"
            size="middle"
            footer={({ onClose }) => <Button onClick={onClose}>Close</Button>}
        >
            {isPending ? (
                <Skeleton active />
            ) : (
                <div className="flex flex-col gap-4">
                    <Card
                        type="inner"
                        size="small"
                        title={<CardTitle icon={Receipt} title="Summary" />}
                        classNames={{ body: 'grid grid-cols-1 gap-x-6 gap-y-4' }}
                    >
                        <VReadOnly
                            label="Allocated amount"
                            field="amount"
                            formStore={excessPaymentFormStore}
                            formatValueFn={(value) => formatCurrency(value)}
                        />
                    </Card>

                    <Card
                        type="inner"
                        size="small"
                        title={<CardTitle icon={FileText} title="Details" />}
                        classNames={{ body: 'grid max-w-lg grid-cols-2 gap-x-6 gap-y-4' }}
                    >
                        <VReadOnly
                            label="Contribution method"
                            field="method"
                            formStore={excessPaymentFormStore}
                            formatValueFn={(_, item) => (
                                <Tag variant="outlined" color="blue" className="max-w-max text-xs">
                                    {item?.method ? EXCESS_PAYMENT_METHODS_LABELS[item.method] : null}
                                </Tag>
                            )}
                        />
                        <VReadOnly
                            label="Status"
                            field="status"
                            formStore={excessPaymentFormStore}
                            formatValueFn={(_, item) => (
                                <Tag
                                    variant="outlined"
                                    color={item?.status ? statusColors[item.status] : 'blue'}
                                    className="max-w-max text-xs"
                                >
                                    {item?.status ? EXCESS_PAYMENT_STATUSES_LABELS[item.status] : null}
                                </Tag>
                            )}
                        />
                        <VReadOnly
                            label="Included in invoice"
                            field="isExcessIncludedInInvoice"
                            formStore={excessPaymentFormStore}
                            formatValueFn={(value) => toYesNo(value)}
                        />
                        <VReadOnly
                            label="Insurer approved"
                            field="isInsurerApproved"
                            formStore={excessPaymentFormStore}
                            formatValueFn={(value) => toYesNo(value)}
                        />
                    </Card>
                </div>
            )}
        </OverlayPage>
    );
}
